import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterLink, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Subject, takeUntil } from 'rxjs';
import { CompanyService } from '../../../../modules/companies/services/company.service';
import { CandidateService } from 'app/modules/candidates/services/candidate.service';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { cloneDeep } from 'lodash-es';
import { AuthService } from 'app/core/auth/auth.service';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        NotificationsComponent,
        UserComponent,
        MatIconModule,
        MatButtonModule,
        LanguagesComponent,
        FuseFullscreenComponent,
        SearchComponent,
        ShortcutsComponent,
        MessagesComponent,
        RouterOutlet,
        QuickChatComponent,
        RouterLink,
    ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    user: User;
    isCompanyEnabled: boolean = false;
    isCondidateEnabled: boolean = false;
    isCompanyStatusLoaded: boolean = false;

    companyId: number | null = null;
    condidateId: number | null = null;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _companyService: CompanyService,
        private _condidateService: CandidateService,
        public _authService: AuthService, // Public to access in template
    ) {
    }

    get currentYear(): number {
        return new Date().getFullYear();
    }

    ngOnInit(): void {
        // Load company status on init if user is the company owner (collaborators get a 403 on companies/by-email)
        const email = localStorage.getItem('email');
        if (email && this._authService.isCompanyOwner()) {
            this._companyService.loadCompanyStatus(email).subscribe();
        }
        
        // Load candidate status on init if user is a candidate
        if (email && this._authService.isCandidate()) {
            this._condidateService.loadCandidateStatus(email).subscribe();
        }

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
                this._updateNavigationDisabledState();
            });

        // Subscribe to the user service
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        // Écouter les changements du statut de la société
        this._companyService.companyStatus$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(status => {
                this.isCompanyEnabled = status.enabled;
                this.companyId = status.id;
                this.isCompanyStatusLoaded = status.id !== null;
                this._updateNavigationDisabledState();
            });
            this._condidateService.candidateStatus$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(status => {
                this.isCondidateEnabled = status.enabled;
                this.condidateId = status.id;
                this._updateNavigationDisabledState();
            });
    }


    private _updateNavigationDisabledState(): void {
        if (!this.navigation) {
            return;
        }

        if (this._authService.isSuperAdmin() || this._authService.isResource()) {
            return;
        }

        const navigationClone = cloneDeep(this.navigation);
        
        const shouldDisable = (this._authService.isCompany() && this.isCompanyStatusLoaded && !this.isCompanyEnabled) ||
                              (this._authService.isCandidate() && !this.isCondidateEnabled);

        this._disableNavigationItems(navigationClone.default, shouldDisable);

        this.navigation = navigationClone;
    }

 
    private _disableNavigationItems(items: FuseNavigationItem[], shouldDisable: boolean): void {
        if (!items) {
            return;
        }

        items.forEach(item => {
            const hasEditPageLink = this._hasEditPageLink(item);
            
            if (shouldDisable && !hasEditPageLink) {
                item.disabled = true;
            } else {
                item.disabled = false;
            }

            if (item.children) {
                this._disableNavigationItems(item.children, shouldDisable);
            }
        });
    }


    private _hasEditPageLink(item: FuseNavigationItem): boolean {
        if (item.link) {
            if (this._authService.isCompany() && this.companyId) {
                const companyEditPattern = `/companies/edit/${this.companyId}`;
                if (item.link === companyEditPattern || item.link.startsWith(companyEditPattern + '/')) {
                    return true;
                }
            }
            
            if (this._authService.isCandidate() && this.condidateId) {
                const candidateEditPattern = `/candidates/edit/${this.condidateId}`;
                if (item.link === candidateEditPattern || item.link.startsWith(candidateEditPattern + '/')) {
                    return true;
                }
            }
        }

        if (item.children) {
            return item.children.some(child => this._hasEditPageLink(child));
        }

        return false;
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    toggleNavigation(name: string): void {
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);
        if (navigation) {
            navigation.toggle();
        }
    }
}
